var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Timeouts } from './framework/Timeouts';
export class PanasonicCamera {
    constructor(http, state, log, framework) {
        this.http = http;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.cameraInfo = {};
        this.timeouts = new Timeouts();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.cameras = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return {
                    name: this.cameraInfo[key].name,
                    hasCameraTracking: false,
                    hasPan: true,
                    hasTilt: true,
                    hasZoom: true,
                    location: 'near',
                };
            }),
            getKey: (config) => {
                if (!('id' in config)) {
                    this.log.error(`invalid camera config: ${JSON.stringify(config)}`);
                    return 'invalid';
                }
                return config.id.toString();
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!('id' in config))
                    return 'incompatible';
                const driverKey = config.id.toString();
                return this.cameraInfo[driverKey] === undefined ? 'missing' : 'ok';
            }),
        };
        this.cameraMove = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraMove: unknown key ${JSON.stringify(key)}`);
                    return value;
                }
                this.state.cameraPreset.update(undefined);
                if ('direction' in value)
                    cameraInfo.moveDirection = value.direction;
                if (value.action === 'start') {
                    cameraInfo.moveSpeed = 'slow';
                    yield this.sendCameraMove('start', cameraInfo, tags);
                }
                else if (value.action === 'stop') {
                    yield this.sendCameraMove('stop', cameraInfo, tags);
                }
                else if (value.action === 'repeat') {
                    if (((_a = cameraInfo.moveSpeed) !== null && _a !== void 0 ? _a : 'slow') === 'slow') {
                        // TODO: can we just start again instead of having to stop first
                        yield this.sendCameraMove('stop', cameraInfo, tags);
                        cameraInfo.moveSpeed = 'fast';
                        yield this.sendCameraMove('start', cameraInfo, tags);
                    }
                    else {
                        yield this.sendCameraMove('continue', cameraInfo, tags);
                    }
                }
                return value;
            }),
        };
        this.cameraPosition = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraPosition: unknown key ${JSON.stringify(key)}`);
                    return { pan: 0, tilt: 0, zoom: 0 };
                }
                const match = yield this.httpGet(`APC`, /aPC([0-9a-f]{4})([0-9a-f]{4})/i, tags);
                let pan = 0;
                let tilt = 0;
                let zoom = 0;
                if (match) {
                    pan = parseInt(match[1], 16);
                    tilt = parseInt(match[2], 16);
                }
                const zoomResp = yield this.httpGet(`GZ`, /gz([0-9a-f]+)/i, tags);
                if (zoomResp) {
                    zoom = parseInt(zoomResp[1], 16);
                }
                return { pan: pan, tilt: tilt, zoom: zoom };
            }),
        };
        this.cameraPreset = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.cameraPreset.value;
            }),
            set: (presetId, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b, _c, _d;
                if (presetId === undefined)
                    return undefined;
                const preset = this.getPreset(presetId);
                if (preset === undefined) {
                    this.log.error(`preset "${presetId}" not found`);
                    return undefined;
                }
                const cameraInfo = this.cameraInfo[preset.cameraId.toString()];
                if (cameraInfo === undefined) {
                    this.log.error(`camera not found for preset "${presetId}"`);
                    return undefined;
                }
                // set pan/tilt
                const pan = ((_b = preset.pan) !== null && _b !== void 0 ? _b : 0).toString(16).padStart(4, '0').toUpperCase();
                const tilt = ((_c = preset.tilt) !== null && _c !== void 0 ? _c : 0).toString(16).padStart(4, '0').toUpperCase();
                const zoom = ((_d = preset.zoom) !== null && _d !== void 0 ? _d : 0x555).toString(16).padStart(3, '0').toUpperCase();
                yield this.httpGet(`APC${pan}${tilt}`, /aPC(.{4})(.{4})|eR[0-9a-f]+:APC/i, tags);
                // set zoom
                if (preset.zoom !== undefined) {
                    yield this.httpGet(`AXZ${zoom}`, /aXZ(.{3})|eR[0-9a-f]+:axz/i, tags);
                }
                return presetId;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.httpGet('QSV1', /QSV1(.+)$/i, tags);
                if (match)
                    return match[1];
                return '';
            }),
            powerOffRestrictions: ['get'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.httpGet(`O`, /p(.+)/i, tags);
                return match ? match[1] !== '0' && match[1] !== 'f' : this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                this.state.busy.update(true);
                // Would be nice to be able to restore a 'power off' preset when powering off the camera
                // since this camera doesn't turn around when powered off
                const match = yield this.httpGet(`O${power ? '1' : '0'}`, /p(.+)/i, tags);
                // Prevent other commands from executing after powering up because
                // the camera will reposition itself after the first movement if done too quickly after
                // a power on.
                if (power && !this.state.power.value) {
                    yield this.timeouts.delay(2000);
                }
                this.state.busy.update(false);
                return match ? match[1] !== '0' && match[1] !== 'f' : this.state.power.value;
            }),
        };
        // polling
        this.polls = [{ intervalMs: 3000, controls: ['power'] }];
    }
    // phase callbacks
    onConfigure(config) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.cameraInfo = {};
            this.timeouts.clearAll();
            for (const cameraConfig of Object.values((_a = config.cameras) !== null && _a !== void 0 ? _a : {})) {
                if (!('id' in cameraConfig)) {
                    this.log.error(`invalid camera config: ${JSON.stringify(cameraConfig)}`);
                    continue;
                }
                const driverKey = cameraConfig.id.toString();
                this.cameraInfo[driverKey] = Object.assign(Object.assign({}, cameraConfig), { hasPower: true });
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials, config) {
        return __awaiter(this, void 0, void 0, function* () {
            // Power query
            const match = yield this.httpGet(`O`, /p(.+)/i, ['initialize']);
            if (match)
                return 'connected';
            return 'verifyFailed';
        });
    }
    // other class methods
    getPreset(presetId) {
        var _a;
        for (const [configId, cameraInfo] of Object.entries(this.cameraInfo)) {
            const preset = (_a = cameraInfo.presets) === null || _a === void 0 ? void 0 : _a[presetId];
            if (preset !== undefined) {
                return Object.assign(Object.assign({}, preset), { configId, presetId, cameraId: cameraInfo.id });
            }
        }
        return undefined;
    }
    httpGet(cmd, regex, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = (yield this.http.request('get', `/cgi-bin/aw_ptz?cmd=%23${cmd}&res=1`, Object.assign({}, (tags ? { tags } : undefined))));
            if (regex && resp.data)
                return resp.data.match(regex);
            return resp.data;
        });
    }
    sendCameraMove(action, cameraInfo, tags) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            let cmd = '';
            const panSpeed = cameraInfo.moveSpeed === 'slow'
                ? this.scaleFromVisca('pan', (_a = cameraInfo.panSpeedSlow) !== null && _a !== void 0 ? _a : 3)
                : this.scaleFromVisca('pan', (_b = cameraInfo.panSpeedFast) !== null && _b !== void 0 ? _b : 8);
            const tiltSpeed = cameraInfo.moveSpeed === 'slow'
                ? this.scaleFromVisca('tilt', (_c = cameraInfo.tiltSpeedSlow) !== null && _c !== void 0 ? _c : 1)
                : this.scaleFromVisca('tilt', (_d = cameraInfo.tiltSpeedFast) !== null && _d !== void 0 ? _d : 8);
            const zoomSpeed = cameraInfo.moveSpeed === 'slow'
                ? this.scaleFromVisca('zoom', (_e = cameraInfo.zoomSpeedSlow) !== null && _e !== void 0 ? _e : 1)
                : this.scaleFromVisca('zoom', (_f = cameraInfo.zoomSpeedFast) !== null && _f !== void 0 ? _f : 3);
            const direction = cameraInfo.moveDirection === 'left'
                ? cameraInfo.invertPan
                    ? 'right'
                    : 'left'
                : cameraInfo.moveDirection === 'right'
                    ? cameraInfo.invertPan
                        ? 'left'
                        : 'right'
                    : cameraInfo.moveDirection;
            if (action === 'start') {
                if (direction === 'left') {
                    cmd = `P${panSpeed.toString().padStart(2, '0')}`;
                }
                else if (direction === 'right') {
                    cmd = `P${(100 - panSpeed).toString().padStart(2, '0')}`;
                }
                else if (direction === 'down') {
                    cmd = `T${tiltSpeed.toString().padStart(2, '0')}`;
                }
                else if (direction === 'up') {
                    cmd = `T${(100 - tiltSpeed).toString().padStart(2, '0')}`;
                }
                else if (direction === 'out') {
                    cmd = `Z${zoomSpeed.toString().padStart(2, '0')}`;
                }
                else if (direction === 'in') {
                    cmd = `Z${(100 - zoomSpeed).toString().padStart(2, '0')}`;
                }
                else {
                    this.log.error(`invalid move direction: ${cameraInfo.moveDirection}`);
                    return;
                }
            }
            else if (action === 'stop') {
                if (['left', 'right', 'up', 'down'].includes(direction !== null && direction !== void 0 ? direction : 'none')) {
                    cmd = `PTS5050`;
                }
                else if (['in', 'out'].includes(direction !== null && direction !== void 0 ? direction : 'none')) {
                    cmd = `Z50`;
                }
                else {
                    this.log.error(`invalid move direction: ${cameraInfo.moveDirection}`);
                    return;
                }
            }
            else if (action === 'continue') {
                // nop
            }
            if (cameraInfo.moveTimeout) {
                cameraInfo.moveTimeout.clear();
                cameraInfo.moveTimeout = undefined;
            }
            if (cmd !== '') {
                yield this.httpGet(cmd, /[tpz]S\d+|eR[0-9a-f]+.+/i, tags);
            }
            if (action === 'start' || action === 'continue') {
                cameraInfo.moveTimeout = this.timeouts.set(() => __awaiter(this, void 0, void 0, function* () {
                    cameraInfo.moveTimeout = undefined;
                    this.log.error(`camera move timeout for camera id ${cameraInfo.id}`);
                    yield this.sendCameraMove('stop', cameraInfo, tags);
                }), 1000);
            }
        });
    }
    // Since this driver is based on VISCA camera motion, the pan/tilt/zoom speeds correspond to
    // VISCA values.  This routine converts them to Panasonic numbers by scaling the range.
    scaleFromVisca(direction, visca) {
        let newValue = 0;
        if (direction === 'pan') {
            // 1 (slow) to 24 (fast) to 49 (slow) to 1 (fast)
            newValue = 50 - Math.round((visca - 1) * (48 / 23) + 1); // pan Left.  pan Right = 100 - pan left.
        }
        else if (direction === 'tilt') {
            newValue = 50 - Math.round((visca - 1) * (48 / 19) + 1); // tilt down, tilt up = 100 - tilt down
        }
        else if (direction === 'zoom') {
            newValue = 50 - Math.round((visca - 1) * (48 / 6) + 1); // wide speed, tele speed = 100 - wide speed
        }
        return newValue;
    }
}
// static properties
PanasonicCamera.driverType = 'httpDriver';
PanasonicCamera.key = '-NFoChVNR7sv1lO2CyIn';
PanasonicCamera.httpRequestOptions = {
    prettyCommands: false,
    scheme: 'http',
};
